import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { useHistory, withRouter } from 'react-router-dom';
import uniqBy from 'lodash.uniqby';
import qs from 'query-string';

import { getCheckoutPreviousCart } from '../../redux/checkoutSelectors';
import CART_ITEM from '../../constants/cartItems';
import { getAsset } from '../../../../util/assets';
import { getAlbumCustomPrintName } from '../../../../util/custom-prints';
import { clearCart as clearCartAction } from '../../redux/checkoutActions';
import Button from '../../../../components/global/Button';
import CustomPrintBadge from '../../../../apps/temp/components/CustomPrintBadge';
import DownloadPhotosHelp, {
  DOWNLOAD_PHOTOS_HELP_OPTIONS,
} from '../../../../apps/temp/views/memberships/CheckoutSuccess/components/DownloadPhotosHelp';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../constants/print-packages';
import {
  isAlbumMembershipGroupUrl,
  isAllPhotosUrl,
  isConsumerUrl,
} from './helpers';
import DismissButton from '../../../../components/global/DismissButton';

const propTypes = {
  clearCart: PropTypes.func.isRequired,
  previousCart: PropTypes.array.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }),
  onButtonClick: PropTypes.func,
  actionProps: PropTypes.shape({
    title: PropTypes.string,
    digitalBundlesConfigured: PropTypes.bool,
  }),
};

const CheckoutCartSuccess = ({
  clearCart,
  previousCart,
  match,
  location,
  onButtonClick,
  onRequestClose,
  actionProps = {},
}) => {
  const { title = 'Thanks for your purchase!', digitalBundlesConfigured } =
    actionProps ?? {};
  const { albumMembershipGroupId } = match.params;
  const history = useHistory();

  // Clear the cart when this component unmounts, i.e. when the uses has
  // navigated away from this page.
  useEffect(() => {
    return () => clearCart();
  }, []);

  const albumFolderSubscriptionItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
  );
  const albumSubscriptionItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_SUBSCRIPTION,
  );
  const albumCustomPrintItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_CUSTOM_PRINT,
  );
  const purchasedCustomPrintItem = previousCart.find(
    item => item.type === CART_ITEM.PURCHASED_CUSTOM_PRINT,
  );
  const albumPrintPackageItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_PRINT_PACKAGE,
  );
  const purchasedPrintPackageItem = previousCart.find(
    item => item.type === CART_ITEM.PURCHASED_PRINT_PACKAGE,
  );
  const individualPrintItem = previousCart.find(
    item => item.type === CART_ITEM.INDIVIDUAL_PRINT,
  );
  const legacyPrintItem = previousCart.find(
    item => item.type === CART_ITEM.LEGACY_PRINT,
  );
  const digitalDownloadItems = previousCart.filter(
    item => item.type === CART_ITEM.DIGITAL_DOWNLOAD,
  );
  const albumPrintPackageItems = previousCart.filter(item =>
    [CART_ITEM.ALBUM_PRINT_PACKAGE, CART_ITEM.PURCHASED_PRINT_PACKAGE].includes(
      item.type,
    ),
  );
  const printProductItems = previousCart.find(
    item => item.type === CART_ITEM.PRINT_PRODUCT,
  );
  const digitalBundleItems = previousCart.filter(
    item => item.type === CART_ITEM.DIGITAL_BUNDLE,
  );

  const { isGreenScreenEnabled = false, hierarchyItemId } = useMemo(
    () =>
      albumSubscriptionItem ??
      albumFolderSubscriptionItem ??
      [
        ...(digitalBundleItems ?? []),
        ...(digitalDownloadItems ?? []),
        ...(albumPrintPackageItems ?? []),
      ].find(item => !!item.isGreenScreenEnabled) ??
      {},
    [digitalBundleItems, digitalDownloadItems],
  );

  const photoIds = useMemo(() => {
    const ids = [];
    (digitalBundleItems ?? []).forEach(item => {
      (item?.photos ?? []).forEach(photo => {
        ids.push(photo?.id);
      });
    });
    (digitalDownloadItems ?? []).forEach(item => {
      ids.push(item?.photo?.id);
    });

    return uniqBy(ids).filter(Boolean);
  }, [digitalDownloadItems, digitalBundleItems]);

  const canConfigureAccountCardDownloads = useMemo(
    () => !!isGreenScreenEnabled && !!hierarchyItemId,
    [isGreenScreenEnabled, hierarchyItemId],
  );

  const printPackagesWithDownload = previousCart.find(
    item =>
      [
        CART_ITEM.ALBUM_PRINT_PACKAGE,
        CART_ITEM.PURCHASED_PRINT_PACKAGE,
      ].includes(item.type) &&
      (item.albumPrintPackage.print_package.digital_downloads_mode ===
        DIGITAL_DOWNLOADS_MODE.INCLUDED ||
        item.includeDigitalDownload ||
        !!(item.albumPrintPackage?.print_package?.items?.records ?? [])?.find(
          i => i.grants_purchased_identity || !!i.digital_bundle,
        )),
  );

  const purchasedAlbumMembershipGroupId =
    albumFolderSubscriptionItem?.albumFolder.id ||
    albumSubscriptionItem?.album.id;

  const hasPrints =
    !!albumPrintPackageItem ||
    !!purchasedPrintPackageItem ||
    !!purchasedCustomPrintItem ||
    !!individualPrintItem ||
    !!legacyPrintItem ||
    !!printProductItems ||
    !!albumCustomPrintItem?.configuration;

  const hasPurchasedDownloads =
    !!digitalDownloadItems?.length ||
    !!digitalBundleItems?.length ||
    !!digitalBundlesConfigured ||
    !!purchasedAlbumMembershipGroupId ||
    !!printPackagesWithDownload;

  const hasPurchasedGreenScreenDownloads = !!previousCart.find(
    item =>
      [
        CART_ITEM.ALBUM_PRINT_PACKAGE,
        CART_ITEM.PURCHASED_PRINT_PACKAGE,
      ].includes(item.type) && !!item.digitalDownloadAccountCard?.id,
  );

  return (
    <div className="memberships-checkout-success-wrap">
      {onRequestClose && (
        <DismissButton
          onClick={onRequestClose}
          className="memberships-checkout-dismiss"
        />
      )}
      <div className="memberships-checkout-success-header">
        <div className="d-flex align-items-center mb-3">
          <img
            src={getAsset('/waldo-excited.png')}
            width="60px"
            className="mx-auto"
            alt="Waldo Icon"
          />
        </div>

        <h2 className="text-extra-bold text-center">{title}</h2>

        {hasPrints && (
          <div className="text-md text-center">
            Your prints will be delivered soon!
          </div>
        )}

        {!hasPurchasedDownloads && (
          <div className="memberships-checkout-success-copy">
            <Button
              className="mt-2 btn-memberships-checkout-success-custom-print"
              replace
              btnBlock
              btnStyle="info"
              btnSize="lg"
              onClick={() => onButtonClick?.()}
            >
              {isAlbumMembershipGroupUrl(location)
                ? 'Get back to your photos'
                : 'Get back to Waldo'}
            </Button>
          </div>
        )}

        <div className="memberships-checkout-success-extra groove-blue">
          {albumCustomPrintItem && !albumCustomPrintItem?.configuration && (
            <div className="memberships-checkout-success-custom-print">
              <h3 className="text-extra-bold">
                <CustomPrintBadge className="mr-1" />

                <span>
                  Configure your{' '}
                  {getAlbumCustomPrintName(
                    albumCustomPrintItem?.albumCustomPrint,
                  )}
                  !
                </span>
              </h3>

              <Button
                btnBlock
                btnStyle="info"
                btnSize="lg"
                className="btn-memberships-checkout-success-custom-print"
                to="/consumer/custom-prints/redirect"
                onClick={() => onButtonClick?.()}
              >
                Start here
              </Button>
            </div>
          )}

          {canConfigureAccountCardDownloads && (
            <Fragment>
              <p className="text-center mt-2">
                Choose your backgrounds and cropping for digital purchases!
              </p>
              <Button
                btnBlock
                className="mb-3"
                onClick={() => {
                  history.push({
                    pathname: `/cards/downloads-configurator/${hierarchyItemId}`,
                    search: `?${qs.stringify(
                      {
                        photoIds,
                      },
                      { arrayFormat: 'bracket' },
                    )}`,
                  });
                  onRequestClose?.();
                }}
              >
                Configure now
              </Button>
              <p className="text-center text-xs">
                Or, check your email for a link to configure later
              </p>
            </Fragment>
          )}

          {isAlbumMembershipGroupUrl(location) &&
            hasPurchasedDownloads &&
            !canConfigureAccountCardDownloads && (
              <DownloadPhotosHelp
                onViewPhotos={() => {
                  onButtonClick?.();

                  const availableAlbumMembershipGroupId =
                    purchasedAlbumMembershipGroupId ?? albumMembershipGroupId;

                  if (isConsumerUrl(location)) {
                    history.replace(
                      `/consumer/matches/${availableAlbumMembershipGroupId}${
                        isAllPhotosUrl(
                          location,
                          availableAlbumMembershipGroupId,
                          true,
                        )
                          ? '/all'
                          : ''
                      }`,
                    );
                    return;
                  }

                  if (availableAlbumMembershipGroupId) {
                    history.replace(
                      `/app/shutterbug/hierarchy-items/${availableAlbumMembershipGroupId}${
                        isAllPhotosUrl(
                          location,
                          availableAlbumMembershipGroupId,
                          false,
                        )
                          ? ''
                          : '/matches'
                      }`,
                    );
                  }
                }}
                options={[
                  DOWNLOAD_PHOTOS_HELP_OPTIONS.APP_DOWNLOAD,
                  hasPurchasedGreenScreenDownloads
                    ? undefined
                    : DOWNLOAD_PHOTOS_HELP_OPTIONS.BROWSER,
                  digitalDownloadItems ||
                  digitalBundleItems ||
                  digitalBundlesConfigured ||
                  printPackagesWithDownload
                    ? DOWNLOAD_PHOTOS_HELP_OPTIONS.EMAIL
                    : undefined,
                ].filter(Boolean)}
              />
            )}

          <img
            src={getAsset('/groove-blue.svg')}
            alt=""
            className="memberships-checkout-success-groove"
          />
        </div>
      </div>
    </div>
  );
};

CheckoutCartSuccess.propTypes = propTypes;

const mapStateToProps = state => ({
  previousCart: getCheckoutPreviousCart(state),
});

const mapDispatchToProps = {
  // eslint-disable-next-line no-undef
  clearCart: clearCartAction,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(CheckoutCartSuccess);
